import { useForm } from "@mantine/form";
import { useState } from "react";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  getAddress,
  getBirthdate,
  getCivility,
  getEmail,
  getlicenseDate,
  getPhone,
  PhoneType,
  updateUserData,
  UserDetails,
} from "@/interfaces/userDetails";
import { CheckmarkIcon } from "@/public/icons/CheckmarkIcon";
import { PenIcon } from "@/public/icons/PenIcon";
import { ProfileValidations } from "@/utils/profileValidation";
import { ProfileInput } from "./profileInput/ProfileInput";
// import { DocumentButton } from "./documentButton/DocumentButton";
import { UpdateProfileInfos } from "./hooks/useUpdateProfileInfos";
import styles from "./Profile.module.scss";

dayjs.extend(customParseFormat);

export enum InputType {
  TEXT = "text",
  PHONE = "phone",
  DATE = "date",
  SELECT = "select",
}

type ProfileProps = {
  userData: UserDetails;
  userToken?: string;
  email: string;
};

export const Profile = ({ userData, userToken, email }: ProfileProps) => {
  const intl = useIntl();
  const [isEditable, setIsEditable] = useState<boolean>(false);

  // Temporary until ProfileInput rework into multiple inputs
  const civility = getCivility(userData);
  const birthDate = getBirthdate(userData);
  const licenseDate = userData.documents ? getlicenseDate(userData) : null;
  const userAddress = getAddress(userData);
  const userEmail = getEmail(userData) ?? email;
  const mobile = getPhone(userData, PhoneType.MOBILE_PHONE);
  const phone = getPhone(userData, PhoneType.FIXED_LINE);

  const form = useForm({
    initialValues: {
      civility: civility,
      lastname: userData.lastname,
      firstname: userData.firstname,
      email: userEmail,
      mobile: mobile,
      phone: phone,
      street: userAddress?.addressDetails,
      additional: userAddress?.additionalAddress,
      zipCode: userAddress?.zipCode,
      city: userAddress?.city,
      country: userAddress?.country,
      birthDate: birthDate,
      nationality: userData.birthPlace?.nationality,
      birthCountry: userData.birthPlace?.country,
      birthCity: userData.birthPlace?.city,
      birthDepartmentNumber: userData.birthPlace?.departmentNumber,
      licenseId: userData.documents?.drivingLicense?.drivingLicenseNumber,
      licenseDate: licenseDate,
      licenseCity: userData.documents?.drivingLicense?.obtainingPlace,
      licenseCountry:
        userData.documents?.drivingLicense?.drivingLicenseNationality,
    },
    validate: {
      lastname: (value) => ProfileValidations.required(value),
      firstname: (value) => ProfileValidations.required(value),
      mobile: (value) => ProfileValidations.phone(value),
      phone: (value) => ProfileValidations.phone(value),
      birthDate: (value) => ProfileValidations.date(value),
      licenseDate: (value) => ProfileValidations.date(value),
    },
  });

  const validateForm = () => {
    const formValidation = form.validate();

    if (!formValidation.hasErrors) {
      const updatedUserData = updateUserData(userData, form);

      UpdateProfileInfos(updatedUserData, userToken)
        .then(() => {
          setIsEditable(false);
        })
        .catch((e) => {
          console.error("Update error", e);
        });
    } else {
      console.log(formValidation.errors);
    }
  };

  return (
    <div className={styles.profile}>
      <div className={styles.actions}>
        {!isEditable && (
          <button className={styles.edit} onClick={() => setIsEditable(true)}>
            <div className={styles.buttonIcon}>
              <PenIcon color={"#3f4756"} />
            </div>
            {intl.formatMessage({ id: "edit" })}
          </button>
        )}
        {isEditable && (
          <button className={styles.validate} onClick={() => validateForm()}>
            <div className={styles.buttonIcon}>
              <CheckmarkIcon color={"white"} />
            </div>
            {intl.formatMessage({ id: "validate" })}
          </button>
        )}
      </div>
      <form className={styles.form} onSubmit={() => validateForm()}>
        <div className={styles.sections}>
          <div className={styles.identity}>
            <div className={styles.sectionLabel}>
              {intl.formatMessage({ id: "identity" })}
            </div>
            <ProfileInput
              label="civility"
              isEditable={isEditable}
              type={InputType.SELECT}
              form={form}
            />
            <ProfileInput
              label="lastname"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="firstname"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput label="email" type={InputType.TEXT} form={form} />
            <ProfileInput
              label="mobile"
              isEditable={isEditable}
              type={InputType.PHONE}
              form={form}
            />
            <ProfileInput
              label="phone"
              isEditable={isEditable}
              type={InputType.PHONE}
              form={form}
            />
          </div>
          {/* <div className={styles.address}>
            <div className={styles.sectionLabel}>
              {intl.formatMessage({ id: "address" })}
            </div>
            <ProfileInput
              label="street"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="additional"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="zipCode"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="city"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="country"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            {userData.documents?.domicileProofs?.documentFiles && (
              <div className={styles.document}>
                <DocumentButton
                  files={userData.documents.domicileProofs.documentFiles}
                />
              </div>
            )}
          </div> */}
        </div>
        <div className={styles.sections}>
          {/* <div className={styles.civilStatus}>
            <div className={styles.sectionLabel}>
              {intl.formatMessage({ id: "civilStatus" })}
            </div>
            <ProfileInput
              label="birthDate"
              isEditable={isEditable}
              type={InputType.DATE}
              form={form}
            />
            <ProfileInput
              label="nationality"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="birthCountry"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="birthCity"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="birthDepartmentNumber"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            {userData.documents?.identityPieces?.documentFiles && (
              <div className={styles.document}>
                <DocumentButton
                  files={userData.documents.identityPieces.documentFiles}
                />
              </div>
            )}
          </div>
          <div className={styles.license}>
            <div className={styles.sectionLabel}>
              {intl.formatMessage({ id: "license" })}
            </div>
            <ProfileInput
              label="licenseId"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="licenseDate"
              isEditable={isEditable}
              type={InputType.DATE}
              form={form}
            />
            <ProfileInput
              label="licenseCity"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            <ProfileInput
              label="licenseCountry"
              isEditable={isEditable}
              type={InputType.TEXT}
              form={form}
            />
            {userData.documents?.drivingLicenses?.documentFiles && (
              <div className={styles.document}>
                <DocumentButton
                  files={userData.documents.drivingLicenses.documentFiles}
                />
              </div>
            )}
          </div> */}
        </div>
      </form>
    </div>
  );
};
